import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowDown,
  Compass,
  Aperture,
  TrendingUp,
  Layers,
  Handshake,
} from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { Section, SectionHeading } from '@/components/layout/Section';
import { Reveal } from '@/components/layout/Reveal';
import { CtaButton, TextLink } from '@/components/site/CtaButton';
import { WhatsAppButton } from '@/components/site/WhatsAppButton';
import { PhotoFrame } from '@/components/site/PhotoFrame';
import { StatBand } from '@/components/site/StatBand';
import { HomeServicesSticky } from '@/components/site/HomeServicesSticky';
import { TestimonialCard } from '@/components/site/TestimonialCard';
import { CaseStudyCard } from '@/components/site/CaseStudyCard';
import { ProcessSteps } from '@/components/site/ProcessSteps';
import { CtaBlock } from '@/components/site/CtaBlock';
import { AGENCY, CONSULTATION_PATH, IMAGES } from '@/lib/site';
import { useI18n } from '@/lib/i18n';

const VALUE_ICONS = { Compass, Aperture, TrendingUp, Layers, Handshake };
const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const reduce = useReducedMotion();
  const { t } = useI18n();
  const h = t.home.hero;

  const fade = (delay) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.85, delay, ease: EASE },
        };

  return (
    <section
      data-testid="home-hero"
      className="relative overflow-hidden"
      style={{ backgroundColor: 'var(--rmh-canvas)' }}
    >
      <span
        aria-hidden="true"
        className="rmh-champagne-wash pointer-events-none absolute inset-0"
      />
      <Container wide className="relative z-10 pb-16 pt-12 sm:pb-20 sm:pt-16 lg:pb-28 lg:pt-20">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <motion.div {...fade(0)} className="flex items-center gap-3">
              <span
                className="block h-px w-10"
                style={{ backgroundColor: 'var(--rmh-accent)' }}
                aria-hidden="true"
              />
              <span className="rmh-eyebrow">{h.eyebrow}</span>
            </motion.div>

            <motion.h1
              {...fade(0.08)}
              data-testid="home-hero-title"
              className="rmh-display mt-7 max-w-[16ch] text-[2.4rem] sm:text-[3.4rem] lg:text-[4.4rem] xl:text-[4.8rem]"
            >
              {h.titleBefore}
              <span className="relative inline-block">
                {h.titleAccent}
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 -bottom-[0.08em] block h-[2px]"
                  style={{ backgroundColor: 'var(--rmh-accent)' }}
                />
              </span>
              {h.titleAfter}
            </motion.h1>

            <motion.p
              {...fade(0.16)}
              className="rmh-body mt-8 max-w-xl text-[0.98rem] sm:text-[1.05rem]"
            >
              {h.lede}
            </motion.p>

            <motion.div
              {...fade(0.24)}
              className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <CtaButton
                to={CONSULTATION_PATH}
                size="lg"
                variant="primary"
                arrow
                testId="home-hero-book-consultation-button"
                className="w-full sm:w-auto"
              >
                {t.common.bookConsultation}
              </CtaButton>
              <CtaButton
                to="/services"
                size="lg"
                variant="secondary"
                testId="home-hero-explore-services-button"
                className="w-full sm:w-auto"
              >
                {t.common.exploreServices}
              </CtaButton>
              <WhatsAppButton
                variant="ghost"
                size="lg"
                testId="home-hero-whatsapp-link"
                className="w-full sm:w-auto"
              />
            </motion.div>

            <motion.div {...fade(0.32)} className="mt-12 max-w-md">
              <div className="rmh-rule" />
              <div className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-2">
                <p className="text-[0.8rem] text-[color:var(--rmh-muted)]">
                  <span className="rmh-num text-[color:var(--rmh-ink)]">500K+</span>{' '}
                  {h.statViews}
                </p>
                <p className="text-[0.8rem] text-[color:var(--rmh-muted)]">
                  <span className="rmh-num text-[color:var(--rmh-ink)]">160K+</span>{' '}
                  {h.statReach}
                </p>
              </div>
            </motion.div>
          </div>

          <motion.div
            {...(reduce
              ? {}
              : {
                  initial: { opacity: 0, y: 24 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 1, delay: 0.18, ease: EASE },
                })}
            className="lg:col-span-5"
          >
            <div className="relative mx-auto max-w-[440px] lg:max-w-none">
              <PhotoFrame
                src={IMAGES.heroPortrait}
                webp={IMAGES.heroPortraitWebp}
                alt={t.photo.homeHeroAlt}
                ratio="4 / 5"
                objectPosition="50% 42%"
                eager
                caption={t.photo.homeHeroCaption}
              />
              <div
                className="absolute -bottom-5 -left-4 hidden max-w-[220px] rounded-[14px] border px-5 py-4 lg:-left-12 lg:block"
                style={{
                  backgroundColor: 'var(--rmh-surface)',
                  borderColor: 'var(--rmh-border)',
                  boxShadow: 'var(--shadow-lift)',
                }}
              >
                <p className="rmh-eyebrow text-[0.55rem]">{h.nowOffering}</p>
                <p className="mt-2 font-serif text-[0.98rem] leading-snug text-[color:var(--rmh-ink)]">
                  {h.aiTeaser}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div {...fade(0.5)} className="mt-16 hidden lg:flex">
          <a
            href="#services"
            data-testid="home-hero-scroll-cue"
            className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.2em] text-[color:var(--rmh-muted-2)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
          >
            <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
            {t.common.scroll}
          </a>
        </motion.div>
      </Container>
    </section>
  );
};

export default function Home() {
  const { t } = useI18n();

  return (
    <>
      <Hero />
      <StatBand />

      {/* ------------------------------- services ------------------------------- */}
      {/* Service 05 (Websites & AI automation) lives in this list. The full
          CloudService.hr story — capabilities and live references — is kept on
          the Services page so the landing page stays uncluttered. */}
      <HomeServicesSticky />

      {/* --------------------------- about romilda ------------------------------ */}
      <Section tone="canvas2" data-testid="home-about-section" wide>
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <div className="mx-auto max-w-[420px] lg:max-w-none">
              <PhotoFrame
                src={IMAGES.aboutPortrait}
                webp={IMAGES.aboutPortraitWebp}
                alt={t.photo.homeAboutAlt}
                ratio="4 / 5"
                objectPosition="50% 25%"
                caption={t.photo.homeAboutCaption}
              />
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal>
              <SectionHeading
                eyebrow={t.home.about.eyebrow}
                title={t.home.about.title}
              />
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-6 flex flex-col gap-5">
                <p className="rmh-body text-[0.95rem] sm:text-base">{t.home.about.p1}</p>
                <p className="rmh-body text-[0.95rem] sm:text-base">{t.home.about.p2}</p>
                <blockquote
                  className="mt-2 border-l-2 pl-5"
                  style={{ borderColor: 'var(--rmh-accent)' }}
                >
                  <p className="font-serif text-[1.12rem] leading-snug text-[color:var(--rmh-ink)] sm:text-[1.28rem]">
                    {t.home.about.quote}
                  </p>
                </blockquote>
              </div>
            </Reveal>
            <Reveal delay={0.14}>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <CtaButton
                  to="/about"
                  variant="secondary"
                  size="md"
                  testId="home-about-read-story-button"
                >
                  {t.home.about.readStory}
                </CtaButton>
                <CtaButton
                  to={CONSULTATION_PATH}
                  variant="primary"
                  size="md"
                  arrow
                  testId="home-about-book-consultation-button"
                >
                  {t.common.bookConsultation}
                </CtaButton>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* ---------------------------- why work with us -------------------------- */}
      <Section tone="canvas" data-testid="home-why-section" wide>
        <Reveal>
          <SectionHeading
            eyebrow={t.home.why.eyebrow}
            title={t.home.why.title}
            lede={t.home.why.lede}
          />
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {t.valueProps.map((item, i) => {
            const Icon = VALUE_ICONS[item.icon] || Compass;
            return (
              <Reveal key={item.title} delay={i * 0.06}>
                <div
                  data-testid={`home-value-prop-${i}`}
                  className="border-t pt-6"
                  style={{ borderColor: 'var(--rmh-border)' }}
                >
                  <Icon
                    className="h-5 w-5"
                    strokeWidth={1.4}
                    style={{ color: 'var(--rmh-mocha)' }}
                    aria-hidden="true"
                  />
                  <h3 className="mt-5 font-serif text-[1.16rem] text-[color:var(--rmh-ink)]">
                    {item.title}
                  </h3>
                  <p className="rmh-body mt-3 text-[0.9rem]">{item.body}</p>
                </div>
              </Reveal>
            );
          })}

          <Reveal delay={0.3}>
            <div
              className="flex h-full flex-col justify-between rounded-[16px] border p-7"
              style={{
                backgroundColor: 'var(--rmh-ink)',
                borderColor: 'var(--rmh-ink)',
              }}
              data-testid="home-value-prop-ai-card"
            >
              <div>
                <p className="rmh-eyebrow" style={{ color: 'rgba(250,247,242,0.6)' }}>
                  {t.home.why.aiTag}
                </p>
                <h3
                  className="mt-5 font-serif text-[1.24rem] leading-snug"
                  style={{ color: 'var(--rmh-canvas)' }}
                >
                  {t.home.why.aiTitle}
                </h3>
                <p
                  className="mt-3 text-[0.9rem] leading-relaxed"
                  style={{ color: 'rgba(250,247,242,0.72)' }}
                >
                  {t.home.why.aiBody}
                </p>
              </div>
              <p
                className="mt-6 text-[0.72rem] uppercase tracking-[0.16em]"
                style={{ color: 'var(--rmh-accent)' }}
              >
                {t.home.why.aiHint}
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* -------------------------------- results ------------------------------- */}
      <Section tone="canvas2" data-testid="home-results-section" wide>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-8">
            <Reveal>
              <SectionHeading
                eyebrow={t.home.results.eyebrow}
                title={t.home.results.title}
                lede={t.home.results.lede}
              />
            </Reveal>

            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {t.caseStudies.map((study, i) => (
                <Reveal key={study.title} delay={i * 0.07}>
                  <CaseStudyCard study={study} testId={`home-case-study-card-${i}`} />
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.1} className="hidden lg:col-span-4 lg:block">
            <div className="lg:sticky lg:top-32">
              <PhotoFrame
                src={IMAGES.editorialPortrait}
                webp={IMAGES.editorialPortraitWebp}
                alt={t.photo.homeEditorialAlt}
                ratio="3 / 4"
                objectPosition="50% 55%"
                caption={t.photo.homeEditorialCaption}
              />
              <p className="rmh-body mt-6 text-[0.88rem]">{t.home.results.sideNote}</p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* ----------------------------- testimonials ----------------------------- */}
      <Section tone="canvas" data-testid="home-testimonials-section" wide>
        <Reveal>
          <SectionHeading
            eyebrow={t.home.testimonials.eyebrow}
            title={t.home.testimonials.title}
            lede={t.home.testimonials.lede}
          />
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {t.testimonials.slice(0, 3).map((item, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <TestimonialCard testimonial={item} testId={`home-testimonial-card-${i}`} />
            </Reveal>
          ))}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          {t.testimonials.slice(3, 5).map((item, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <TestimonialCard
                testimonial={item}
                testId={`home-testimonial-card-${i + 3}`}
              />
            </Reveal>
          ))}
        </div>
      </Section>

      {/* -------------------------------- process ------------------------------- */}
      <Section
        tone="canvas"
        className="pt-0 lg:pt-0"
        data-testid="home-process-section"
        wide
      >
        <Reveal>
          <SectionHeading
            eyebrow={t.home.process.eyebrow}
            title={t.home.process.title}
            lede={t.home.process.lede}
          />
        </Reveal>
        <ProcessSteps />
      </Section>

      <CtaBlock />

      {/* footer teaser link row */}
      <Section tone="canvas" className="py-12 lg:py-14" wide>
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <p className="rmh-body text-[0.9rem]">
              {t.home.bottom.prefix}{' '}
              <Link
                to="/services"
                data-testid="home-bottom-services-link"
                className="rmh-link-underline text-[color:var(--rmh-ink)]"
              >
                {t.home.bottom.servicesLink}
              </Link>{' '}
              {t.common.or}{' '}
              <Link
                to="/about"
                data-testid="home-bottom-about-link"
                className="rmh-link-underline text-[color:var(--rmh-ink)]"
              >
                {t.home.bottom.aboutLink}
              </Link>
              .
            </p>
            <a
              href={`mailto:${AGENCY.email}`}
              data-testid="home-bottom-email-link"
              className="rmh-link-underline text-[0.84rem] text-[color:var(--rmh-muted)]"
            >
              {AGENCY.email}
            </a>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
